import { OutlinedSelectOptionType, Select, TextInput } from '@octano/global-ui';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { AcademicLevelListItem } from '../../../../api/requests/schools';
import { SectionTitle } from '../../../../components/text';
import TooltipForSelect from '../../../../components/tooltip/TooltipForSelect';
import { useValidations } from '../../../../hooks/useValidations';
import useValidatorNotes from '../../../../hooks/useValidatorNotes';
import { formatToOnlyPositiveIntegers } from '../../../../utils/currency';
import { StudyPlanValues } from './StudyPlanForm';

export interface StudyPlanFormSiesProps {
  readOnly?: boolean;
  academicAreas: AcademicLevelListItem[];
  admissionRequirements: AcademicLevelListItem[];
}

const StudyPlanFormSies = (props: StudyPlanFormSiesProps) => {
  const { readOnly, academicAreas, admissionRequirements } = props;

  const { t } = useTranslation();
  const prefix = 'studyPlans.form';

  const { control, watch } = useFormContext<StudyPlanValues>();

  const { config } = useValidatorNotes();

  const [selectedHighSchoolGradesAdmission] = watch([
    'highSchoolGradesAdmission',
  ]);

  const { msgValidations, rulesPatternDecimal } = useValidations();

  const approvedStates: OutlinedSelectOptionType[] = useMemo(() => {
    return [
      {
        label: t(`${prefix}.yes`),
        value: 1,
      },
      {
        label: t(`${prefix}.no`),
        value: 0,
      },
    ];
  }, [t, prefix]);

  const ishighSchoolGradesAdmissionApply = useMemo(() => {
    return selectedHighSchoolGradesAdmission?.value === 1;
  }, [selectedHighSchoolGradesAdmission]);

  return (
    <Fragment>
      <Row>
        <Col xs={12}>
          <SectionTitle text={t(`${prefix}.siesTitle`)} />
          <Row>
            <Col xs={12} md={4} className="pb-3">
              <TooltipForSelect
                placement="right"
                hasValue={true}
                isClearable={true}
                id="admissionRequirement"
                text={t(`${prefix}.admissionRequirementTooltip`)}
              >
                <Select
                  name="admissionRequirement"
                  label={t(`${prefix}.admissionRequirement`)}
                  options={admissionRequirements?.map((e) => ({
                    label: e.name,
                    value: e.id,
                  }))}
                  control={control}
                  disabled={readOnly}
                />
              </TooltipForSelect>
            </Col>
            <Col xs={12} md={4} className="pb-3">
              <TextInput
                name="recognizedSemesters"
                tooltip={t(`${prefix}.recognizedSemestersTooltip`)}
                label={t(`${prefix}.recognizedSemesters`)}
                control={control}
                placeholder="0"
                rules={{
                  pattern: rulesPatternDecimal,
                }}
                formatter={formatToOnlyPositiveIntegers}
              />
            </Col>
            <Col xs={12} md={4} className="pb-3">
              <Select
                name="academicArea"
                label={t(`${prefix}.currentAcademicArea`)}
                options={academicAreas?.map((e) => ({
                  label: e.name,
                  value: e.id,
                }))}
                control={control}
                disabled={readOnly}
              />
            </Col>
            <Col xs={12} md={4} className="pb-3"></Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <SectionTitle
            text={t(`${prefix}.admissionTitle`)}
            tooltipId="admissionTitle"
            tooltipText={t(`${prefix}.admissionTitleTooltip`)}
          />
          <Row>
            <Col xs={12} md={4} className="pb-3">
              <Select
                name="highSchoolDiplomaAdmission"
                label={t(`${prefix}.highSchoolDiplomaAdmission`)}
                options={approvedStates}
                control={control}
                disabled={readOnly}
              />
            </Col>
            <Col xs={12} md={4} className="pb-3">
              <Select
                name="highSchoolGradesAdmission"
                label={t(`${prefix}.highSchoolGradesAdmission`)}
                options={approvedStates}
                control={control}
                disabled={readOnly}
              />
            </Col>
            {ishighSchoolGradesAdmissionApply && (
              <Col xs={12} md={4} className="pb-3">
                <TextInput
                  name="minimumHighSchoolGPA"
                  label={t(`${prefix}.minimumHighSchoolGPA`)}
                  control={control}
                  placeholder="0"
                  rules={{
                    pattern: rulesPatternDecimal,
                    validate: (value: any) => {
                      if (!value) return msgValidations.invalidIntNumeric;
                      if (
                        Number(value) < config.pass ||
                        Number(value) > config.max
                      )
                        return msgValidations.invalidBtw(
                          config.pass,
                          config.max,
                        );
                      return true;
                    },
                  }}
                  formatter={formatToOnlyPositiveIntegers}
                />
              </Col>
            )}
            <Col xs={12} md={4} className="pb-3">
              <Select
                name="priorLearningRecognitionAdmission"
                label={t(`${prefix}.priorLearningRecognitionAdmission`)}
                options={approvedStates}
                control={control}
                disabled={readOnly}
              />
            </Col>
            <Col xs={12} md={4} className="pb-3">
              <Select
                name="workExperienceAdmission"
                label={t(`${prefix}.workExperienceAdmission`)}
                options={approvedStates}
                control={control}
                disabled={readOnly}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Fragment>
  );
};

export default StudyPlanFormSies;
